import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import type { Team } from '@core/types/team/Team';
import { listChildTeams } from '@/features/team/children';

const MAX_DEPTH = 3;

function Branch({ teamId, depth, visited }: { teamId: string; depth: number; visited: Set<string> }) {
  const { t, locale } = useI18n();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    setTeams([]);
    setExpanded(new Set());
    listChildTeams(teamId)
      .then((items) => items.filter((item) => item.entity?.id && !visited.has(item.entity.id)))
      .then((items) => items.sort((a, b) => a.name.localeCompare(b.name, locale)))
      .then((items) => { if (alive) setTeams(items); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [teamId, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p className="px-4 py-4 text-sm text-muted-foreground" role="status">{t('common.loading')}</p>;
  if (error) return <p className="px-4 py-4 text-sm text-muted-foreground">{t('team.overview.childTeamsError')}</p>;
  if (teams.length === 0) return depth === 1 ? <p className="px-4 py-4 text-sm text-muted-foreground">{t('team.overview.childTeamsEmpty')}</p> : null;

  return (
    <ul className="space-y-2">
      {teams.map((team) => {
        const id = team.entity!.id;
        const isExpanded = expanded.has(id);
        return (
          <li key={id}>
            <div className="flex items-start gap-2 rounded-xl border border-border bg-card p-3">
              {depth < MAX_DEPTH && (
                <button
                  type="button"
                  aria-label={`${isExpanded ? t('team.hierarchy.collapse') : t('team.hierarchy.expand')}: ${team.name}`}
                  aria-expanded={isExpanded}
                  onClick={() => setExpanded((current) => {
                    const next = new Set(current);
                    if (next.has(id)) next.delete(id); else next.add(id);
                    return next;
                  })}
                  className="mt-0.5 h-7 w-7 flex-none rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span aria-hidden="true">{isExpanded ? '−' : '+'}</span>
                </button>
              )}
              <Link to={`/teams/${id}`} className="min-w-0 flex-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="block text-sm font-medium text-foreground">{team.name}</span>
                {team.description && <span className="mt-0.5 block line-clamp-2 text-xs text-muted-foreground">{team.description}</span>}
              </Link>
            </div>
            {isExpanded && depth < MAX_DEPTH && (
              <div className="ml-5 mt-2 border-l border-border pl-4">
                <Branch teamId={id} depth={depth + 1} visited={new Set([...visited, id])} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function TeamHierarchyTab({ teamId, teamName }: { teamId: string; teamName: string }) {
  const { t } = useI18n();
  return (
    <section className="mx-auto max-w-3xl space-y-5" aria-labelledby="team-hierarchy-heading">
      <div>
        <h2 id="team-hierarchy-heading" className="text-xl font-semibold text-foreground">{t('team.hierarchy.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('team.hierarchy.description')}</p>
      </div>
      <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm font-semibold text-foreground">{teamName}</div>
      <Branch teamId={teamId} depth={1} visited={new Set([teamId])} />
    </section>
  );
}
